import React, { useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useClaimRebuild } from '../../store/hooks';

interface ClaimRebuildButtonProps {
  claimId: string;
  claimStatus?: string;
  disabled?: boolean;
  iconSize?: 'small' | 'medium' | 'large';
  onRebuildStart?: () => void;
}

export const ClaimRebuildButton: React.FC<ClaimRebuildButtonProps> = ({
  claimId,
  claimStatus,
  disabled = false,
  iconSize = 'medium',
  onRebuildStart,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { rebuildClaim, isRebuilding } = useClaimRebuild();
  const { billing: billingMetrics } = useEventTracking();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleRebuild = async () => {
    setDialogOpen(false);

    const result = await rebuildClaim(claimId);
    if (result.success) {
      billingMetrics.rebuildClaim(claimId, {
        claim_status: claimStatus,
      });
      onRebuildStart?.();
      enqueueSnackbar('Claim rebuild started. This may take a moment.', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to rebuild claim. Please try again.', { variant: 'error' });
    }
  };

  const iconSizeMap = {
    small: '1rem',
    medium: '1.2rem',
    large: '1.5rem',
  };

  return (
    <>
      <Tooltip title="Rebuild Claim" placement="bottom" arrow>
        <span>
          <IconButton
            onClick={() => setDialogOpen(true)}
            disabled={disabled || isRebuilding}
            size={iconSize}
          >
            <RefreshIcon sx={{ fontSize: iconSizeMap[iconSize] }} />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Rebuild claim?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will re-pull all patient, insurance, and provider data from
            current records and regenerate the claim. Any manual edits you have
            made will be overwritten.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRebuild} color="warning">
            Rebuild
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
